import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Auto-incremented Issue No. for the Results page top nav.
 *
 * Counts how many outfits the signed-in user has created up to and including
 * the currently-viewed outfit (chronological ordinal — the user's 1st outfit
 * is Issue No. 1, their 2nd is 2, etc.).
 *
 * Guests always get 1 — they don't have an outfit history.
 *
 * Memoised per outfitId for the lifetime of the hook (stored in a ref-keyed
 * cache) so navigating back to the same outfit doesn't re-query.
 */
export function useIssueNumber(
  outfitId: string | null | undefined,
  isGuest: boolean,
): number {
  const [issue, setIssue] = useState<number>(1);
  const cacheRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!outfitId) {
      setIssue(1);
      return;
    }
    if (isGuest) {
      setIssue(1);
      return;
    }
    const cached = cacheRef.current.get(outfitId);
    if (cached !== undefined) {
      setIssue(cached);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data: userRes } = await supabase.auth.getUser();
        const userId = userRes?.user?.id;
        if (!userId) {
          if (!cancelled) setIssue(1);
          return;
        }

        // Fetch the current outfit's created_at, then count user's outfits
        // created on or before that timestamp.
        const { data: current } = await supabase
          .from("outfits")
          .select("created_at")
          .eq("id", outfitId)
          .maybeSingle();
        if (!current?.created_at) {
          if (!cancelled) setIssue(1);
          return;
        }

        const { count } = await supabase
          .from("outfits")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .lte("created_at", current.created_at);

        const n = Math.max(1, count ?? 1);
        cacheRef.current.set(outfitId, n);
        if (!cancelled) setIssue(n);
      } catch {
        if (!cancelled) setIssue(1);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [outfitId, isGuest]);

  return issue;
}
